/**
 * /customer/history — paginated transaction history (Phase 2b).
 *
 * Per D1=B this is reachable via direct URL only in 2b; the nav
 * entry-point lands in 2d via Profile Quick Access.
 *
 * Filters (all server-side via `/api/me/transactions/history`):
 *   - type chip:  All / Stamps earned / Rewards redeemed
 *   - brand chip: multi-select limited to joined brands (cards cache)
 *   - date preset: Last 7 / 30 / 90 days / All time
 *   - search:     debounced 400ms → ?q=
 *
 * Per D4=A: Load More appends, preserves scroll position (no top-jump).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Coffee, Gift, Search, RotateCcw } from 'lucide-react';
import { api, resolveImageUrl } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';

const PAGE_SIZE = 50;
const DATE_PRESETS = [
  { key: 'all', label: 'All time', days: null },
  { key: '7d', label: 'Last 7 days', days: 7 },
  { key: '30d', label: 'Last 30 days', days: 30 },
  { key: '90d', label: 'Last 90 days', days: 90 },
];

function fromIso(days) {
  if (!days) return null;
  const d = new Date(Date.now() - days * 86400000);
  return d.toISOString();
}

function formatDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function CustomerHistoryPage() {
  const token = useAuthStore((s) => s.token);
  const qc = useQueryClient();

  // Filter state ------------------------------------------------------------
  const [typeFilter, setTypeFilter] = useState('all'); // all | earned | redeemed
  const [brandIds, setBrandIds] = useState([]); // multi-select (joined brands)
  const [datePreset, setDatePreset] = useState('all'); // matches DATE_PRESETS.key
  const [searchInput, setSearchInput] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  // Debounce search input → debouncedQ
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Brand chip list = brands the customer has joined
  const cardsQ = useQuery({
    queryKey: qk.customerCards(),
    queryFn: () => api.customerCards(token),
    enabled: !!token,
  });
  const joinedBrands = useMemo(
    () =>
      (cardsQ.data ?? [])
        .map((c) => c.brand)
        .filter((b) => b && b.id),
    [cardsQ.data],
  );

  // Compose the server filter — single-select for `type` since the
  // backend accepts one. Multi-brand is handled client-side over the
  // returned set (the endpoint supports a single `?brand_id=` but the
  // operator spec asked for multi-select brand chips — we resolve by
  // letting the server widen when the multi-select has exactly 1 brand,
  // and falling back to client-side filter when >1 are picked).
  const fromAt = fromIso(DATE_PRESETS.find((p) => p.key === datePreset)?.days);
  const serverFilters = useMemo(
    () => ({
      type: typeFilter === 'all' ? undefined : typeFilter,
      brand_id: brandIds.length === 1 ? brandIds[0] : undefined,
      from: fromAt || undefined,
      q: debouncedQ || undefined,
      limit: PAGE_SIZE,
    }),
    [typeFilter, brandIds, fromAt, debouncedQ],
  );

  // Pages state for Load More (append + preserve scroll). The cache
  // key is the serverFilters object (without offset); we track all
  // pages locally so adding a new page doesn't trigger a remount of
  // the prior rows.
  const [pages, setPages] = useState([]); // array of arrays
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const filtersKey = useMemo(() => JSON.stringify(serverFilters), [serverFilters]);

  // First-page query — re-runs on filter change. Replaces the page
  // stack with the fresh first page.
  const firstPageQ = useQuery({
    queryKey: qk.customerTransactionsHistory({ ...serverFilters, offset: 0 }),
    queryFn: () => api.customerTransactionsHistory(token, { ...serverFilters, offset: 0 }),
    enabled: !!token,
    refetchOnMount: 'always',
  });

  useEffect(() => {
    if (firstPageQ.data) {
      setPages([firstPageQ.data]);
      setOffset(firstPageQ.data.length);
      setHasMore(firstPageQ.data.length === PAGE_SIZE);
    }
    // intentionally key on filtersKey + the response identity
  }, [firstPageQ.data, filtersKey]);

  // Client-side multi-brand filter (the server only takes 1).
  const allRows = useMemo(() => {
    const flat = pages.flat();
    if (brandIds.length <= 1) return flat;
    const set = new Set(brandIds);
    return flat.filter((r) => set.has(r.brand_id));
  }, [pages, brandIds]);

  const toggleBrand = (id) =>
    setBrandIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const resetFilters = () => {
    setTypeFilter('all');
    setBrandIds([]);
    setDatePreset('all');
    setSearchInput('');
  };

  const onLoadMore = async () => {
    if (!token || loadingMore || !hasMore) return;
    setLoadingMore(true);
    // Preserve scroll position — capture before fetch + restore after
    // the new page is appended (browser keeps the prior content in
    // place; we just guarantee no programmatic scroll on our side).
    const prevScrollY = window.scrollY;
    try {
      const next = await api.customerTransactionsHistory(token, {
        ...serverFilters,
        offset,
      });
      // Append to cache too so a refetch sees the merged set if
      // intermediate invalidations happen.
      qc.setQueryData(
        qk.customerTransactionsHistory({ ...serverFilters, offset: 0 }),
        (prev) => (Array.isArray(prev) ? [...prev, ...next] : next),
      );
      setPages((cur) => [...cur, next]);
      setOffset((cur) => cur + next.length);
      setHasMore(next.length === PAGE_SIZE);
      // Restore scrollY on the next tick to defeat any layout shift.
      requestAnimationFrame(() => window.scrollTo({ top: prevScrollY }));
    } finally {
      setLoadingMore(false);
    }
  };

  const filtersDirty =
    typeFilter !== 'all' ||
    brandIds.length > 0 ||
    datePreset !== 'all' ||
    !!debouncedQ;

  return (
    <div className="space-y-6" data-testid="customer-history-screen">
      <div>
        <div className="text-xs uppercase tracking-widest text-gold font-mono mb-2">
          Activity
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-bold">History</h1>
        <p className="text-sm text-white/55 mt-1.5">
          Every stamp earned and reward redeemed, across all your cards.
        </p>
      </div>

      {/* Type filter chips */}
      <div className="flex flex-wrap gap-2">
        <FilterChip
          testID="customer-history-filter-all"
          active={typeFilter === 'all'}
          onClick={() => setTypeFilter('all')}
        >
          All
        </FilterChip>
        <FilterChip
          testID="customer-history-filter-earned"
          active={typeFilter === 'earned'}
          onClick={() => setTypeFilter('earned')}
        >
          Stamps earned
        </FilterChip>
        <FilterChip
          testID="customer-history-filter-redeemed"
          active={typeFilter === 'redeemed'}
          onClick={() => setTypeFilter('redeemed')}
        >
          Rewards redeemed
        </FilterChip>
      </div>

      {/* Brand chips */}
      {joinedBrands.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {joinedBrands.map((b) => {
            const on = brandIds.includes(b.id);
            return (
              <button
                key={b.id}
                type="button"
                onClick={() => toggleBrand(b.id)}
                data-testid={`customer-history-brand-chip-${b.slug ?? b.id}`}
                className={
                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ' +
                  (on
                    ? 'bg-gold/15 border-gold/45 text-gold'
                    : 'bg-[#15151B] border-white/10 text-white/65 hover:text-white hover:border-white/20')
                }
              >
                {b.logo_url ? (
                  <img
                    src={resolveImageUrl(b.logo_url)}
                    alt=""
                    className="w-4 h-4 rounded-md"
                  />
                ) : null}
                {b.name}
              </button>
            );
          })}
        </div>
      ) : null}

      {/* Date presets */}
      <div className="flex flex-wrap gap-2">
        {DATE_PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => setDatePreset(p.key)}
            data-testid={`customer-history-date-preset-${p.key}`}
            className={
              'inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ' +
              (datePreset === p.key
                ? 'bg-gold/10 border-gold/40 text-gold'
                : 'bg-[#15151B] border-white/10 text-white/65 hover:text-white hover:border-white/20')
            }
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-white/35"
        />
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search invoice number or note"
          data-testid="customer-history-search"
          className="w-full pl-8 pr-3 py-2 rounded-xl bg-[#15151B] border border-white/10 text-sm text-white placeholder-white/30 focus:outline-none focus:border-gold/40"
        />
      </div>

      {filtersDirty ? (
        <button
          type="button"
          onClick={resetFilters}
          className="inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-gold"
        >
          <RotateCcw size={12} /> Reset filters
        </button>
      ) : null}

      {/* Timeline list */}
      {firstPageQ.isLoading && pages.length === 0 ? (
        <div className="h-40 rounded-2xl bg-white/[0.03] border border-white/5 animate-pulse" />
      ) : allRows.length === 0 ? (
        <div
          className="rounded-2xl border border-white/8 bg-[#15151B] px-5 py-12 text-center"
          data-testid="customer-history-empty-state"
        >
          <Coffee size={28} className="mx-auto text-gold/55 mb-3" />
          <div className="text-sm font-medium">No activity yet</div>
          <div className="text-xs text-white/45 mt-1">
            Visit a partner store to start earning.
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-white/8 bg-[#15151B] divide-y divide-white/5">
          {allRows.map((r) => {
            const delta = r.stamps_delta ?? r.delta ?? 0;
            const earned = r.type === 'earned' || delta > 0;
            return (
              <div
                key={r.id}
                data-testid={`customer-history-row-${r.id}`}
                className="flex items-center gap-3 px-5 py-3.5"
              >
                <div
                  className={
                    'w-9 h-9 rounded-full grid place-items-center shrink-0 ' +
                    (earned
                      ? 'bg-gold/15 text-gold'
                      : 'bg-rose-400/10 text-rose-300')
                  }
                >
                  {earned ? <Coffee size={14} /> : <Gift size={14} />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">
                    {earned
                      ? `+${delta} stamps · ${r.brand_name ?? ''}`
                      : `Reward redeemed (${Math.abs(delta)} stamps) · ${r.brand_name ?? ''}`}
                  </div>
                  <div className="text-xs text-white/45 truncate">
                    {[r.invoice_number, r.note, r.branch_name]
                      .filter(Boolean)
                      .join(' · ') || '—'}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  {r.amount_rupiah != null ? (
                    <div className="text-xs text-white/65 font-mono">
                      Rp {Number(r.amount_rupiah).toLocaleString('id-ID')}
                    </div>
                  ) : null}
                  <div className="text-[11px] text-white/40 font-mono">
                    {formatDateTime(r.created_at)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Load more */}
      {hasMore && allRows.length > 0 ? (
        <div className="flex justify-center pt-2">
          <button
            type="button"
            onClick={onLoadMore}
            disabled={loadingMore}
            data-testid="customer-history-load-more"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-white/5 border border-white/12 text-sm text-white/80 hover:bg-gold/10 hover:border-gold/35 hover:text-gold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function FilterChip({ active, onClick, testID, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testID}
      className={
        'inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ' +
        (active
          ? 'bg-gold/15 border-gold/45 text-gold'
          : 'bg-[#15151B] border-white/10 text-white/65 hover:text-white hover:border-white/20')
      }
    >
      {children}
    </button>
  );
}
